import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';

interface Category {
  _id: string;
  category_id: string;
  label: {
    en: string;
    'zh-hk'?: string;
    'zh-tw'?: string;
    'zh-cn'?: string;
    ja?: string;
    ko?: string;
  };
  order: number;
  createdAt: string;
  updatedAt: string;
}

type LabelCode = keyof Category['label'];

// Supported label languages, driven from a single list (mirrors how the
// Localization page renders one input per language) so adding a locale here is
// the only change needed. `en` is required; the rest are optional — the apps
// fall back to English when a translation is missing.
const LABEL_LANGUAGES: { code: LabelCode; name: string; required?: boolean }[] = [
  { code: 'en', name: 'English', required: true },
  { code: 'zh-hk', name: 'Traditional Chinese (Hong Kong)' },
  { code: 'zh-tw', name: 'Traditional Chinese (Taiwan)' },
  { code: 'zh-cn', name: 'Simplified Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
];

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Empty draft for the inline "new category" row/card.
const emptyNewRow = (): Record<string, string> => ({ category_id: '', en: '', 'zh-hk': '', 'zh-tw': '', 'zh-cn': '', ja: '', ko: '' });

// Shared input styles.
const editInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px',
  backgroundColor: 'var(--color-input)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-accent-strong)',
  borderRadius: '4px',
  fontSize: '13px',
};

const newInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  backgroundColor: 'var(--color-input)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '13px',
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  // Inline cell editing (click a label to edit in place — no modal).
  const [editing, setEditing] = useState<{ id: string; code: LabelCode } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Inline "new category" draft.
  const [newRow, setNewRow] = useState<Record<string, string>>(emptyNewRow());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Escape cancels the delete confirmation. An open inline cell editor handles
  // its own Escape (and calls preventDefault), so the two never collide.
  useEscapeToClose(!!deleteConfirm, () => setDeleteConfirm(null));

  const fetchCategories = async (page: number = 1, search: string = '') => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '100',
        ...(search && { search })
      });

      const response = await apiRequest<{
        success: boolean;
        data: Category[];
        pagination: PaginationInfo;
        error?: string;
      }>(`/api/admin/categories?${params}`);

      if (response.success) {
        setCategories(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search') as string;
    setSearchQuery(search);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        `/api/admin/categories/${id}`,
        { method: 'DELETE' }
      );

      if (response.success) {
        fetchCategories(currentPage, searchQuery);
        setDeleteConfirm(null);
      } else {
        setError(response.error || 'Failed to delete category');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  // ── Inline cell editing ────────────────────────────────────────────────
  const startEdit = (id: string, code: LabelCode, current: string) => {
    setEditing({ id, code });
    setEditValue(current || '');
  };

  const saveCell = async (category: Category, code: LabelCode, raw: string) => {
    const value = raw.trim();
    if (code === 'en' && !value) {
      setError('English label is required.');
      return;
    }
    // No-op if unchanged.
    if (value === (category.label[code] || '')) {
      setEditing(null);
      return;
    }

    // Send the full label object — the backend replaces label wholesale.
    const nextLabel: Category['label'] = { ...category.label };
    if (value) nextLabel[code] = value;
    else delete (nextLabel as Partial<Category['label']>)[code];

    const cellId = `${category._id}|${code}`;
    setSavingCell(cellId);
    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        `/api/admin/categories/${category._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: nextLabel })
        }
      );
      if (response.success) {
        setCategories((prev) => prev.map((c) => (c._id === category._id ? { ...c, label: nextLabel } : c)));
        setEditing(null);
      } else {
        setError(response.error || 'Failed to update category');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSavingCell(null);
    }
  };

  // ── Inline create ──────────────────────────────────────────────────────
  const handleCreate = async () => {
    const category_id = newRow.category_id.trim();
    if (!category_id) {
      setError('Category ID is required.');
      return;
    }
    if (!newRow.en.trim()) {
      setError('English label is required.');
      return;
    }

    const label: Category['label'] = { en: newRow.en.trim() };
    for (const lang of LABEL_LANGUAGES) {
      if (lang.code === 'en') continue;
      const v = (newRow[lang.code] || '').trim();
      if (v) label[lang.code] = v;
    }

    setCreating(true);
    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        '/api/admin/categories',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ category_id, label })
        }
      );
      if (response.success) {
        setNewRow(emptyNewRow());
        fetchCategories(currentPage, searchQuery);
      } else {
        setError(response.error || 'Failed to create category');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  // ── Drag reorder ─────────────────────────────────────────────────────────
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newCategories = [...categories];
    const draggedItem = newCategories[draggedIndex];
    newCategories.splice(draggedIndex, 1);
    newCategories.splice(index, 0, draggedItem);

    setCategories(newCategories);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    const reorderedCategories = categories.map((cat, index) => ({
      id: cat._id,
      order: index
    }));

    try {
      const response = await apiRequest<{ success: boolean; error?: string }>(
        '/api/admin/categories/reorder',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categories: reorderedCategories })
        }
      );

      if (response.success) {
        fetchCategories(currentPage, searchQuery);
      } else {
        setError(response.error || 'Failed to reorder categories');
        fetchCategories(currentPage, searchQuery);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder categories');
      fetchCategories(currentPage, searchQuery);
    }

    setDraggedIndex(null);
  };

  // ── Editable-label cell (shared by table + mobile card) ──────────────────
  const renderEditableLabel = (category: Category, code: LabelCode) => {
    const isEditing = editing?.id === category._id && editing?.code === code;
    const cellId = `${category._id}|${code}`;
    const value = category.label[code];
    const isSaving = savingCell === cellId;

    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            autoFocus
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
              if (e.key === 'Enter') { e.preventDefault(); saveCell(category, code, editValue); }
            }}
            style={{ ...editInputStyle, width: 'auto', flex: 1, minWidth: 0 }}
          />
          <button
            onClick={() => saveCell(category, code, editValue)}
            disabled={isSaving}
            title="Confirm (Enter)"
            style={{ flexShrink: 0, padding: '5px 8px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: isSaving ? 'default' : 'pointer', fontSize: '13px', lineHeight: 1, opacity: isSaving ? 0.6 : 1 }}
          >
            {isSaving ? '…' : '✓'}
          </button>
          <button
            onClick={() => setEditing(null)}
            disabled={isSaving}
            title="Cancel (Esc)"
            style={{ flexShrink: 0, padding: '5px 8px', backgroundColor: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: isSaving ? 'default' : 'pointer', fontSize: '13px', lineHeight: 1, opacity: isSaving ? 0.6 : 1 }}
          >
            ✕
          </button>
        </div>
      );
    }

    return (
      <div
        onClick={() => startEdit(category._id, code, value || '')}
        title="Click to edit"
        style={{
          minHeight: '20px',
          padding: '4px 8px',
          borderRadius: '4px',
          cursor: 'text',
          color: value ? 'var(--color-text)' : 'var(--color-subtle)',
          fontStyle: value ? 'normal' : 'italic',
          whiteSpace: 'pre-wrap',
        }}
      >
        {isSaving ? '…saving' : (value || '— add —')}
      </div>
    );
  };

  if (loading && categories.length === 0) {
    return (
      <div className="page-status">Loading categories...</div>
    );
  }

  const thStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid var(--color-border)',
    color: 'var(--color-text)',
    whiteSpace: 'nowrap',
  };

  return (
    <div className="page">
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Categories</h1>

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', flex: '1 1 240px', minWidth: 0, justifyContent: 'flex-end' }}>
          <input
            type="text"
            name="search"
            placeholder="Search categories..."
            defaultValue={searchQuery}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-input)',
              color: 'var(--color-text)',
              borderRadius: '4px',
              fontSize: '14px',
              flex: '1 1 200px',
              minWidth: 0,
            }}
          />
          <button
            type="submit"
            style={{ padding: '8px 16px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
          >
            Search
          </button>
        </form>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={{ marginBottom: '16px', color: 'var(--color-muted)', fontSize: '14px' }}>
        {pagination ? `Showing ${categories.length} of ${pagination.total} categories • ` : ''}
        Drag rows to reorder • Click a label to edit it in place
      </div>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {categories.map((category, index) => (
            <div
              key={category._id}
              draggable={editing?.id !== category._id}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                backgroundColor: draggedIndex === index ? '#1f6feb20' : 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '10px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ color: 'var(--color-subtle)', cursor: 'move', fontSize: '16px', lineHeight: '1.4', flexShrink: 0 }}>⋮⋮</span>
                <div style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: '13px', color: 'var(--color-muted)', wordBreak: 'break-all' }}>{category.category_id}</div>
                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-accent-strong)', color: '#ffffff', fontSize: '12px', fontWeight: '500', flexShrink: 0 }}>
                  #{category.order}
                </span>
              </div>
              {LABEL_LANGUAGES.map((lang) => (
                <div key={lang.code} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px' }}>
                  <span style={{ color: 'var(--color-subtle)', width: '52px', flexShrink: 0, paddingTop: '4px', textTransform: 'uppercase', fontSize: '11px' }}>{lang.code}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>{renderEditableLabel(category, lang.code)}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px' }}>
                <button
                  onClick={() => setDeleteConfirm(category._id)}
                  style={{ flex: 1, padding: '8px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}

          {/* Inline add-new card */}
          <div style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px dashed var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '14px' }}>+ New Category</div>
            <input
              type="text"
              placeholder="category_id (e.g. portrait)"
              value={newRow.category_id}
              onChange={(e) => setNewRow({ ...newRow, category_id: e.target.value })}
              style={{ ...newInputStyle, fontFamily: 'monospace' }}
            />
            {LABEL_LANGUAGES.map((lang) => (
              <input
                key={lang.code}
                type="text"
                placeholder={`Label (${lang.name})${lang.required ? ' *' : ''}`}
                value={newRow[lang.code]}
                onChange={(e) => setNewRow({ ...newRow, [lang.code]: e.target.value })}
                style={newInputStyle}
              />
            ))}
            <button
              onClick={handleCreate}
              disabled={creating}
              style={{ padding: '8px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: creating ? 'default' : 'pointer', fontSize: '13px', fontWeight: '500', opacity: creating ? 0.6 : 1 }}
            >
              {creating ? 'Adding…' : 'Add Category'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
          <table style={{ width: '100%', minWidth: '1180px', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
              <tr>
                <th style={{ ...thStyle, width: '40px' }}></th>
                <th style={thStyle}>Category ID</th>
                {LABEL_LANGUAGES.map((lang) => (
                  <th key={lang.code} style={thStyle}>{lang.name}</th>
                ))}
                <th style={{ ...thStyle, textAlign: 'center', width: '60px' }}>Order</th>
                <th style={{ ...thStyle, textAlign: 'center', width: '90px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category, index) => (
                <tr
                  key={category._id}
                  draggable={editing?.id !== category._id}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    backgroundColor: draggedIndex === index ? '#1f6feb20' : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px', textAlign: 'center', color: 'var(--color-subtle)', cursor: 'move' }}>⋮⋮</td>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '14px', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                    {category.category_id}
                  </td>
                  {LABEL_LANGUAGES.map((lang) => (
                    <td key={lang.code} style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                      {renderEditableLabel(category, lang.code)}
                    </td>
                  ))}
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-accent-strong)', color: '#ffffff', fontSize: '12px', fontWeight: '500' }}>
                      {category.order}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => setDeleteConfirm(category._id)}
                      style={{ padding: '4px 12px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {/* Inline add-new row */}
              <tr style={{ backgroundColor: 'var(--color-surface-secondary)' }}>
                <td style={{ padding: '8px', textAlign: 'center', color: 'var(--color-subtle)' }}>+</td>
                <td style={{ padding: '8px' }}>
                  <input
                    type="text"
                    placeholder="category_id"
                    value={newRow.category_id}
                    onChange={(e) => setNewRow({ ...newRow, category_id: e.target.value })}
                    style={{ ...newInputStyle, fontFamily: 'monospace' }}
                  />
                </td>
                {LABEL_LANGUAGES.map((lang) => (
                  <td key={lang.code} style={{ padding: '8px' }}>
                    <input
                      type="text"
                      placeholder={lang.required ? 'required' : 'optional'}
                      value={newRow[lang.code]}
                      onChange={(e) => setNewRow({ ...newRow, [lang.code]: e.target.value })}
                      style={newInputStyle}
                    />
                  </td>
                ))}
                <td style={{ padding: '8px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '12px' }}>auto</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <button
                    onClick={handleCreate}
                    disabled={creating}
                    style={{ padding: '4px 12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: creating ? 'default' : 'pointer', fontSize: '12px', fontWeight: 500, opacity: creating ? 0.6 : 1 }}
                  >
                    {creating ? '…' : 'Add'}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '8px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: 'var(--color-text)' }}>Confirm Delete</h3>
            <p style={{ marginBottom: '20px', color: 'var(--color-muted)' }}>
              Are you sure you want to delete this category? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-subtle)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={{ padding: '8px 16px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
